import { ref, onUnmounted } from "vue";

export function useOCR() {
  const text = ref("");
  const progress = ref(0);
  const status = ref("idle"); // 'idle' | 'loading' | 'recognizing' | 'completed' | 'error'
  const error = ref(null);
  
  // Use AbortController to replace the worker.terminate() cancellation logic
  let abortController = null;

  /**
   * Initialize the OCR process on a base64 image via the Go API.
   * @param {string} image - Base64 image string or URL (data URI)
   * @param {string} language - Language code (kept for signature compatibility, ignored by current API)
   */
  const recognize = async (image, language = "eng") => {
    console.log(language)

    // Cancel any ongoing fetch requests
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    // Reset state to match the original Tesseract logic
    text.value = "";
    progress.value = 0;
    error.value = null;
    status.value = "loading";

    try {
      // Simulate progress jumping to 50% since standard HTTP fetches 
      // don't report granular progress like Tesseract workers do.
      status.value = "recognizing";
      progress.value = 50;

      // The API spec requires the raw base64 string without the data URI prefix.
      const cleanBase64 = image.replace(/^data:image\/[a-z]+;base64,/, '');

      const response = await fetch('/api/v1/ocr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_base64: cleanBase64,
        }),
        signal: abortController.signal // Attach the abort signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP Error ${response.status}`);
      }

      const data = await response.json();

      text.value = data.text || "";
      status.value = "completed";
      progress.value = 100;

    } catch (err) {
      // Ignore errors caused by the AbortController cancelling the fetch
      if (err.name === 'AbortError') {
        console.log("OCR fetch aborted");
        return; 
      }
      
      error.value = err.message || "An error occurred during OCR processing";
      status.value = "error";
      console.error("OCR Error:", err);
      
    } finally {
      abortController = null;
    }
  };

  // Safety cleanup if the component using this composable is unmounted while running
  onUnmounted(() => {
    if (abortController) {
      abortController.abort();
    }
  });

  return {
    text,
    progress,
    status,
    error,
    recognize,
  };
}